import {
  deleteV1DealerIdApplicationAppId,
  getV1Applications,
  getV1DealerIdApplicationAppId,
  getV1DealerIdApplicationAppIdSummary,
  postV1ApplicationCopy,
  postV1DealerIdApplicationAppIdCalculateProgress,
  postV1DealerIdApplicationAppIdCoborrower,
  postV1DealerIdApplicationAppIdCoborrowerBorIdAcceptInvite,
  postV1DealerIdApplicationAppIdCoborrowerInvite,
  postV1DealerIdApplicationAppIdEnvelope,
  postV1DealerIdApplicationAppIdReopen,
  postV1DealerIdApplicationBorIdSign,
  postV1DealerIdApplicationBorIdSigned,
  putV1DealerIdApplicationAppId,
  putV1DealerIdApplicationAppIdSummary,
  type DtoAcceptCoBorrowerInviteReq,
  type DtoApplication,
  type DtoApplicationCreateEnvelopeResp,
  type DtoApplicationProgressResp,
  type DtoApplicationSummaryResp,
  type DtoApplicationSummaryUpdateReq,
  type DtoCopyApplicationReq,
  type DtoCreateApplicationResp,
  type DtoCreateCoBorrowerReq,
  type DtoCreateCoBorrowerResp,
  type DtoNoContentResp,
  type DtoSignApplicationReq,
  type DtoSignApplicationResp,
  type DtoUpdateApplicationReq,
} from "@porchpass/api-client";
import {
  assertApiSuccess,
  assertApiSuccessNoContent,
} from "../utils/assertApiSuccess";
import { EMPTY_REQUEST_BODY } from "./constants";
import {
  requireApplicationId,
  requireBorrowerId,
  lazyDealerId,
  type DomainIds,
} from "./types";

export function createApplicationDomain(ids: DomainIds) {
  const getDealerId = lazyDealerId(ids);

  return {
    getApplications: async (dealerName: string): Promise<DtoApplication[]> => {
      const response = await getV1Applications({ dealer_id: dealerName });
      return assertApiSuccess<DtoApplication[]>(response);
    },

    getApplication: async (applicationId?: string): Promise<DtoApplication> => {
      const appId = requireApplicationId(applicationId ?? ids.applicationId);
      const response = await getV1DealerIdApplicationAppId(
        getDealerId(),
        appId,
      );
      return assertApiSuccess<DtoApplication>(response);
    },

    updateApplication: async (
      body: DtoUpdateApplicationReq,
      applicationId?: string,
    ): Promise<DtoApplication> => {
      const appId = requireApplicationId(applicationId ?? ids.applicationId);
      const response = await putV1DealerIdApplicationAppId(
        getDealerId(),
        appId,
        body,
      );
      return assertApiSuccess<DtoApplication>(response);
    },

    deleteApplication: async (applicationId: string): Promise<void> => {
      const response = await deleteV1DealerIdApplicationAppId(
        getDealerId(),
        applicationId,
      );
      assertApiSuccessNoContent(response);
    },

    copyApplication: async (
      body: DtoCopyApplicationReq,
    ): Promise<DtoCreateApplicationResp> => {
      const response = await postV1ApplicationCopy(body);
      return assertApiSuccess<DtoCreateApplicationResp>(response);
    },

    getApplicationSummary: async (
      applicationId?: string,
    ): Promise<DtoApplicationSummaryResp> => {
      const appId = requireApplicationId(applicationId ?? ids.applicationId);
      const response = await getV1DealerIdApplicationAppIdSummary(
        getDealerId(),
        appId,
      );
      return assertApiSuccess<DtoApplicationSummaryResp>(response);
    },

    updateApplicationSummary: async (
      body: DtoApplicationSummaryUpdateReq,
      applicationId?: string,
    ): Promise<DtoApplicationSummaryResp> => {
      const appId = requireApplicationId(applicationId ?? ids.applicationId);
      const response = await putV1DealerIdApplicationAppIdSummary(
        getDealerId(),
        appId,
        body,
      );
      return assertApiSuccess<DtoApplicationSummaryResp>(response);
    },

    calculateProgress: async (
      applicationId?: string,
      options?: RequestInit,
    ): Promise<DtoApplicationProgressResp> => {
      const appId = requireApplicationId(applicationId ?? ids.applicationId);
      const response = await postV1DealerIdApplicationAppIdCalculateProgress(
        getDealerId(),
        appId,
        options,
      );
      return assertApiSuccess<DtoApplicationProgressResp>(response);
    },

    markSigned: async (
      borrowerId: string,
      options?: RequestInit,
    ): Promise<DtoNoContentResp> => {
      const response = await postV1DealerIdApplicationBorIdSigned(
        getDealerId(),
        borrowerId,
        options,
      );
      return assertApiSuccess<DtoNoContentResp>(response);
    },

    reopenApplication: async (
      applicationId?: string,
    ): Promise<DtoNoContentResp> => {
      const appId = requireApplicationId(applicationId ?? ids.applicationId);
      const response = await postV1DealerIdApplicationAppIdReopen(
        getDealerId(),
        appId,
      );
      return assertApiSuccess<DtoNoContentResp>(response);
    },

    createCoborrower: async (
      applicationId?: string,
    ): Promise<DtoCreateCoBorrowerResp> => {
      const appId = requireApplicationId(applicationId ?? ids.applicationId);
      const response = await postV1DealerIdApplicationAppIdCoborrower(
        getDealerId(),
        appId,
        EMPTY_REQUEST_BODY,
      );
      return assertApiSuccess<DtoCreateCoBorrowerResp>(response);
    },

    inviteCoborrower: async (
      body: DtoCreateCoBorrowerReq,
      applicationId?: string,
    ): Promise<DtoCreateCoBorrowerResp> => {
      const appId = requireApplicationId(applicationId ?? ids.applicationId);
      const response = await postV1DealerIdApplicationAppIdCoborrowerInvite(
        getDealerId(),
        appId,
        body,
      );
      return assertApiSuccess<DtoCreateCoBorrowerResp>(response);
    },

    acceptCoborrowerInvite: async (
      body: DtoAcceptCoBorrowerInviteReq,
      applicationId: string,
      borrowerId: string,
    ) => {
      const response =
        await postV1DealerIdApplicationAppIdCoborrowerBorIdAcceptInvite(
          getDealerId(),
          applicationId,
          borrowerId,
          body,
        );
      return assertApiSuccess(response);
    },

    createEnvelope: async (
      applicationId?: string,
    ): Promise<DtoApplicationCreateEnvelopeResp> => {
      const appId = requireApplicationId(applicationId ?? ids.applicationId);
      const response = await postV1DealerIdApplicationAppIdEnvelope(
        getDealerId(),
        appId,
        EMPTY_REQUEST_BODY,
      );
      return assertApiSuccess<DtoApplicationCreateEnvelopeResp>(response);
    },

    signApplication: async (
      body: DtoSignApplicationReq,
      borrowerId?: string,
    ): Promise<DtoSignApplicationResp> => {
      const borId = requireBorrowerId(borrowerId ?? ids.borrowerId);
      const response = await postV1DealerIdApplicationBorIdSign(
        getDealerId(),
        borId,
        body,
      );
      return assertApiSuccess<DtoSignApplicationResp>(response);
    },
  };
}
